/**
 * Audio Constraints Utility
 *
 * ALL AUDIO PROCESSING IS CLIENT-SIDE (on user's device)
 * The server only stores preferences and relays WebRTC signaling.
 * Audio streams are peer-to-peer and never touch the server.
 *
 * This keeps the server lightweight and ensures privacy.
 */

export interface VoiceSettings {
  noiseSuppressionEnabled: boolean;
  echoCancellationEnabled: boolean;
  autoGainControlEnabled: boolean;
  activationMode: 'VOICE_ACTIVITY' | 'PUSH_TO_TALK' | 'ALWAYS_ON';
  activationThreshold: number; // -100 to 0 dB
}

export interface AudioDeviceSettings {
  inputDeviceId: string | null;
  outputDeviceId: string | null;
}

/**
 * Creates MediaStreamConstraints for getUserMedia with voice settings
 * Applied CLIENT-SIDE by the browser's built-in audio processing
 *
 * @param voiceSettings - User's voice preferences (noise suppression, echo cancellation, etc.)
 * @param deviceSettings - Selected audio devices (null = use system default)
 * @returns MediaStreamConstraints for getUserMedia
 */
export const createAudioConstraints = (
  voiceSettings: Partial<VoiceSettings> = {},
  deviceSettings: Partial<AudioDeviceSettings> = {},
): MediaStreamConstraints => {
  const {
    noiseSuppressionEnabled = true,
    echoCancellationEnabled = true,
    autoGainControlEnabled = true,
  } = voiceSettings;

  const { inputDeviceId = null } = deviceSettings;

  return {
    audio: {
      // Device selection (null or 'default' = use system default)
      deviceId: inputDeviceId ? { exact: inputDeviceId } : undefined,

      // Standard WebRTC Audio Processing Constraints
      echoCancellation: echoCancellationEnabled,
      noiseSuppression: noiseSuppressionEnabled,
      autoGainControl: autoGainControlEnabled,

      // High-definition audio parameters
      sampleRate: 48000, // 48kHz studio sampling rate
      sampleSize: 16,
      channelCount: 1, // Mono for clear voice isolation

      // Advanced Voice Isolation & Background Noise Filtering
      // (Supported across Chromium, Firefox, WebKit, iOS/macOS voice isolation)
      // @ts-ignore
      voiceIsolation: noiseSuppressionEnabled,
      // @ts-ignore
      googEchoCancellation: echoCancellationEnabled,
      // @ts-ignore
      googAutoGainControl: autoGainControlEnabled,
      // @ts-ignore
      googNoiseSuppression: noiseSuppressionEnabled,
      // @ts-ignore
      googHighpassFilter: noiseSuppressionEnabled, // Filters low-frequency AC rumble, fan noise (<80Hz)
      // @ts-ignore
      googTypingNoiseDetection: noiseSuppressionEnabled, // Suppresses mechanical keyboard typing noise
      // @ts-ignore
      googAudioMirroring: false,
    },
    video: false,
  };
};

/**
 * Applies audio output device to an HTMLAudioElement
 * Only works in browsers that support setSinkId (Chrome, Edge, Opera)
 *
 * @param audioElement - The audio element to configure
 * @param outputDeviceId - Selected output device (null = use system default)
 */
export const setAudioOutputDevice = async (
  audioElement: HTMLAudioElement,
  outputDeviceId: string | null,
): Promise<void> => {
  // Check if setSinkId is supported
  if ('setSinkId' in audioElement) {
    try {
      await (audioElement as HTMLAudioElement & { setSinkId: (id: string) => Promise<void> }).setSinkId(outputDeviceId || 'default');
    } catch (error) {
      console.error('Error setting audio output device:', error);
    }
  } else {
    console.warn('setSinkId not supported - output device selection unavailable');
  }
};

/**
 * Voice Activity Detection (VAD) utility
 * Detects when user is speaking based on audio levels
 * Runs CLIENT-SIDE using Web Audio API (no server processing)
 *
 * @param stream - MediaStream from getUserMedia
 * @param threshold - Activation threshold in dB (-100 to 0, default -50)
 * @param onActivityChange - Callback when voice activity changes
 * @returns Cleanup function to stop VAD
 */
export const startVoiceActivityDetection = (
  stream: MediaStream,
  threshold: number = -50,
  onActivityChange: (isActive: boolean) => void,
): (() => void) => {
  const audioContext = new AudioContext();
  const analyser = audioContext.createAnalyser();
  const microphone = audioContext.createMediaStreamSource(stream);

  analyser.fftSize = 512;
  analyser.smoothingTimeConstant = 0.8;
  microphone.connect(analyser);

  const dataArray = new Uint8Array(analyser.frequencyBinCount);
  let isActive = false;
  let rafId: number;

  const detectActivity = () => {
    analyser.getByteFrequencyData(dataArray);

    // Calculate average volume
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      sum += dataArray[i];
    }
    const average = sum / dataArray.length;

    // Convert to dB (rough approximation)
    const db = 20 * Math.log10(average / 255) || -100;

    // Check if above threshold
    const nowActive = db > threshold;

    if (nowActive !== isActive) {
      isActive = nowActive;
      onActivityChange(isActive);
    }

    rafId = requestAnimationFrame(detectActivity);
  };

  detectActivity();

  // Cleanup function
  return () => {
    cancelAnimationFrame(rafId);
    microphone.disconnect();
    analyser.disconnect();
    audioContext.close();
  };
};

/**
 * Gets the effective voice settings (user override or server default)
 *
 * @param userPreferences - User's voice preferences (null values = use server default)
 * @param serverDefaults - Server-wide default settings
 * @returns Effective voice settings to use
 */
export const getEffectiveVoiceSettings = (
  userPreferences: {
    voiceNoiseSuppressionEnabled: boolean | null;
    voiceEchoCancellationEnabled: boolean | null;
    voiceAutoGainControlEnabled: boolean | null;
    voiceActivationMode: string | null;
    voiceActivationThreshold: number | null;
  },
  serverDefaults: {
    voiceNoiseSuppressionEnabled: boolean;
    voiceEchoCancellationEnabled: boolean;
    voiceAutoGainControlEnabled: boolean;
    voiceActivationMode: string;
    voiceActivationThreshold: number;
  },
): VoiceSettings => {
  return {
    noiseSuppressionEnabled:
      userPreferences.voiceNoiseSuppressionEnabled ?? serverDefaults.voiceNoiseSuppressionEnabled,
    echoCancellationEnabled:
      userPreferences.voiceEchoCancellationEnabled ?? serverDefaults.voiceEchoCancellationEnabled,
    autoGainControlEnabled:
      userPreferences.voiceAutoGainControlEnabled ?? serverDefaults.voiceAutoGainControlEnabled,
    activationMode:
      (userPreferences.voiceActivationMode as VoiceSettings['activationMode'] | null) ?? serverDefaults.voiceActivationMode,
    activationThreshold:
      userPreferences.voiceActivationThreshold ?? serverDefaults.voiceActivationThreshold,
  };
};
